/**
 * ImageItemCard — shared presentational component matching the Figma "Image Item".
 * Renders a card with a square image area + label. No interaction logic.
 *
 * Used by EntityCard (story creation) and FTUEFlow (onboarding).
 */

import React from "react";
import type { ReactNode } from "react";
import { View, Text, Image, StyleSheet, Platform, Animated } from "react-native";
import type { ImageSourcePropType, StyleProp, ViewStyle } from "react-native";
import { colors, design, textStyle, useScale } from "../../styles";

interface ImageItemCardProps {
  /** Label shown below the image */
  label: string;
  /** Static image source (require() or { uri }) */
  imageSource?: ImageSourcePropType;
  /** Content rendered inside the image area (overrides imageSource) */
  children?: ReactNode;
  /** Scale factor applied to all dimensions (default 1) */
  scale?: number;
  /** Animated opacity for a dim overlay (0 = no dim, 0.5 = faded). Avoids shadow bleed-through from view opacity. */
  dimOpacity?: Animated.Value;
  /** Extra styles merged onto the outer card View */
  style?: StyleProp<ViewStyle>;
}

export default function ImageItemCard({ label, imageSource, children, scale = 1, dimOpacity, style }: ImageItemCardProps) {
  const { s } = useScale();
  const d = design.entityCard;
  const img = design.entityImage;
  const sz = (v: number) => s(v * scale);

  const shadow = Platform.OS === "web"
    ? { boxShadow: `0 ${sz(10)}px ${sz(20)}px rgba(0,0,0,0.2)` }
    : { shadowColor: colors.black, shadowOffset: { width: 0, height: sz(10) }, shadowOpacity: 0.2, shadowRadius: sz(20), elevation: Math.round(8 * scale) };

  return (
    <View style={[styles.card, shadow, { width: sz(d.width), padding: sz(d.padding), borderRadius: sz(d.radius) }, style] as any}>
      <View style={[styles.imageWrap, { width: sz(img.size), height: sz(img.size), borderRadius: sz(img.radius) }]}>
        {children ?? (
          imageSource ? (
            <Image source={imageSource} style={styles.image} resizeMode="cover" resizeMethod="resize" />
          ) : null
        )}
      </View>
      <Text style={[styles.label, { fontSize: sz(design.entityLabel.fontSize), marginTop: sz(4) }]} numberOfLines={1}>{label}</Text>
      {dimOpacity && (
        <Animated.View
          pointerEvents="none"
          style={{ position: "absolute", top: -sz(30), left: -sz(30), right: -sz(30), bottom: -sz(30), backgroundColor: colors.bg, borderRadius: sz(d.radius), opacity: dimOpacity }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
  },
  imageWrap: {
    overflow: "hidden",
    backgroundColor: colors.surfaceHover,
    position: "relative",
  },
  image: {
    width: "100%",
    height: "100%",
  },
  label: {
    ...textStyle.bodyMedium,
    color: colors.text,
    opacity: 0.9,
  },
});
